import * as _ from 'underscore';
import { Keys } from '../helpers/Keys';
import { IColumnWidths } from '../models/ColumnWidths';
import { ISliceable } from '../models/ISliceable';
import { Module } from '../Module';

interface IFilteringListBoxColumnWidthsOptions {
  showActiveRow: boolean;
  showIcons: boolean;
  withCounts: boolean;
}

interface IFilteringListBoxScope extends ng.IScope, IFilteringListBoxColumnWidthsOptions {

  // Bound to attributes
  model: any;
  onItemClicked: (locals: { item: any }) => any;

  // Defined by attributes
  getSliceableSource: (textInput: string) => ISliceable<any>;
  matchPrefixesOnly: boolean;
  textBoxTestId: string;
  placeholderText: string;
  isActiveRow: (item: any) => boolean;
  isRowSeparator: (item: any) => boolean;

  // Internal
  items: ISliceable<any>;
  currentRow: {
    index: number;
    object?: any;
  };
  textInput: string;
  rowClicked: (item: any) => void;
  handleKeyEvent: ($event: BaseJQueryEventObject) => void;
  columnWidths: FilteringListBoxColumnWidths;
  rowHeight: number;
  getItemText: (item: any) => string;
  getItemIcon: (item: any) => string;
}

interface IFilteringListBoxAttributes extends ng.IAttributes {
  matchPrefixesOnly: string;
  itemText: string;
  source: string;
  tbTestId: string;
  isActiveRow: string;
  isRowSeparator: string;
  indicateActiveRow: string;
  itemIcon: string;
  withCounts: string;
  placeholderText: string;
  itemTextFn: string;
}

class FilteringListBoxColumnWidths implements IColumnWidths {
  private widths: number[];
  private ActiveRowColumnWidth = 20; // Width of column that contains the active row icon
  private IconColumnWidth = 26; // Width of icon column, used in favorites menu
  private withCounts: boolean;

  constructor(options: IFilteringListBoxColumnWidthsOptions) {
    this.widths = [];
    if (options.showActiveRow) {
      this.widths.push(this.ActiveRowColumnWidth);
    }
    if (options.showIcons) {
      this.widths.push(this.IconColumnWidth);
    }

    this.withCounts = options.withCounts;
  }

  public getActual(index: number) {
    return this.widths[index];
  }

  public setWidths(currentWidths: number[], containerWidth: number) {
    let optionalColumnWidths = _.reduce(this.widths, (total: number, width: number) => (total + width), 0);
    const countWidth = this.withCounts ? _.last(currentWidths) : 0;
    optionalColumnWidths += countWidth;
    this.widths.push(containerWidth - optionalColumnWidths, countWidth);
  }
}

/**
 * A list box with a text input that filters the list
 *
 * Usage:
 *
 *    tb:filtering-list-box(source="getSiteNames", item-text="name", model="model")
 *
 * Required Arguments:
 *
 *    source:     (textInput: string) => ISliceable<any>. Sliceable that populates the list.
 *    model:      Bound to selection.
 *
 * Optional Arguments:
 *
 *    item-text:           (propertyName: string) The property to use for the displayed text of the list items.
 *    item-text-fn:        (input: function) The function used to get the displayed text of the list items;
 *    item-icon:           (propertyName: string) The property to use for the class of the icon column.
 *    match-prefixes-only: (default=true) If true, matches prefix for each token. Else, match substring
 *    on-item-clicked:     Called when the user clicks on a item
 *    is-row-separator:    (row: any) => boolean. Should return true if there should be a separator below this row
 */
Module.instance().directive('tbFilteringListBox', [
  '$parse', '$timeout', '$translate',
  ($parse: ng.IParseService, $timeout: ng.ITimeoutService,
  $translate: ng.translate.ITranslateService) => {
    return {
      restrict: 'E',
      templateUrl: 'filteringListBox.html',
      replace: true,
      scope: {
        model: '=',
        onItemClicked: '&'
      },
      link: {
        pre: (scope: IFilteringListBoxScope, element: JQuery, attrs: IFilteringListBoxAttributes) => {
          scope.matchPrefixesOnly = attrs.matchPrefixesOnly === 'true';
          scope.showActiveRow = attrs.indicateActiveRow === 'true';
          scope.showIcons = !!attrs.itemIcon;
          scope.placeholderText = attrs.placeholderText || $translate.instant('search');
          scope.getSliceableSource = $parse(attrs.source)(scope.$parent);
          scope.isActiveRow = $parse(attrs.isActiveRow)(scope.$parent);
          scope.isRowSeparator = $parse(attrs.isRowSeparator)(scope.$parent);

          const parsedItemTextFn = attrs.itemTextFn ? $parse(attrs.itemTextFn) : null;

          scope.getItemText = (item: any) => {
            if (attrs.itemText) {
              return item[attrs.itemText];
            } else if (parsedItemTextFn) {
              return parsedItemTextFn(scope.$parent, {row: item});
            } else {
              return item;
            }
          };

          scope.getItemIcon = (item: any) => item[attrs.itemIcon];

          if (attrs.tbTestId) {
            scope.textBoxTestId = attrs.tbTestId + '-textbox';
          }

          scope.withCounts = (attrs.withCounts === 'true');

          scope.rowHeight = 28;
        },
        post: (scope: IFilteringListBoxScope, element: JQuery) => {
          scope.currentRow = {
            index: 0
          };

          scope.rowClicked = (row: any) => {
            if (scope.onItemClicked) {
              scope.onItemClicked({ item: row });
            }

            // When the user clicks on a row, re-focus on the input control
            $timeout(() => element.find('input').focus());
          };

          scope.columnWidths = new FilteringListBoxColumnWidths(scope);

          // Keep the model in sync with the current row in the data-grid
          scope.$watch('currentRow.object', (item: any) => {
            if (item !== undefined) {
              scope.model = item;
            }
          });

          scope.$watch('model', (item: any) => {
            if (item === undefined || item === null) {
              scope.currentRow = { index: -1 };
            }
          });

          scope.$watch('textInput', (value: string) => {
            // reload the values in the listbox when the input changes
            scope.items = scope.getSliceableSource(value || '');

            // If no row is selected and some text has been entered, select the first row
            if (value && scope.currentRow.index === -1) {
              scope.currentRow.index = 0;
            }
          });

          scope.handleKeyEvent = ($event: BaseJQueryEventObject) => {

            switch ($event.which) {

              case Keys.ArrowDown:
                $event.preventDefault();
                scope.currentRow.index++;
                break;

              case Keys.ArrowUp:
                $event.preventDefault();
                if (scope.currentRow.index > 0) {
                  scope.currentRow.index--;
                }
                break;
            }
          };
        }
      }
    };
  }
]);
